/**
 * Live auth-token accessor.
 *
 * The axios `BaseApi` client and the RTK base query must read the SAME token. The RTK base query
 * reads the LIVE Redux value (`state.root.token`); `BaseApi` historically read the *persisted* copy
 * in `localStorage['persist:root']`, which lags the live state by the redux-persist debounce. Right
 * after an IOP (Single Sign-In) login, the `iopLogin` matcher sets the live token immediately, but
 * the persisted copy isn't written until the next flush — so every axios-based service (there are
 * 40+: candidate-portal, company, metrics, …) sent requests with a stale/empty token and got 401s
 * until the flush. RTK calls were unaffected because they read the live value.
 *
 * This accessor lets `BaseApi` read the live token WITHOUT importing the store (which would create a
 * circular dependency: store → apis → services → store). The store registers the getter at startup;
 * `BaseApi` falls back to the persisted copy for the brief window before registration / rehydration.
 */
let tokenGetter: (() => string | null | undefined) | undefined

/** Called once from the store module so non-RTK clients can read the live `state.root.token`. */
export const registerAuthTokenAccessor = (getter: () => string | null | undefined): void => {
  tokenGetter = getter
}

/** The live auth token, or null if unavailable (before the store registers, or when logged out). */
export const getLiveAuthToken = (): string | null => {
  try {
    const token = tokenGetter?.()
    return token && token !== 'null' && token !== '' ? token : null
  } catch {
    return null
  }
}
